import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import {
  FormReportTemplateAdverseMedia,
  FormReportTemplateBusinessAdverseMedia,
  FormReportTemplateBusinessClassification,
  FormReportTemplateBusinessWatchlist,
  FormReportTemplateChainalysisAddressScreening,
  FormReportTemplateCryptoAddressWatchlist,
  FormReportTemplateEquifaxOneview,
  FormReportTemplateGeneric,
  FormReportTemplateKyckrBusinessLookup,
  FormReportTemplateMiddeskBusinessLookup,
  FormReportTemplateMxAccount,
  FormReportTemplatePoliticallyExposedPerson,
  FormReportTemplateSentilinkApplicationRisk,
  FormReportTemplateSentilinkScores,
  FormReportTemplateTrmWalletScreening,
  FormReportTemplateWatchlist,
  ReportTemplateAdverseMediaConfigFormData,
  ReportTemplateBusinessAdverseMediaConfigFormData,
  ReportTemplateBusinessClassificationConfigFormData,
  ReportTemplateBusinessWatchlistConfigFormData,
  ReportTemplateChainalysisAddressScreeningConfigFormData,
  ReportTemplateConfigFormData,
  ReportTemplateCryptoAddressWatchlistConfigFormData,
  ReportTemplateEquifaxOneviewConfigFormData,
  ReportTemplateGenericConfigFormData,
  ReportTemplateKyckrConfigFormData,
  ReportTemplateMiddeskConfigFormData,
  ReportTemplateMxAccountConfigFormData,
  ReportTemplatePoliticallyExposedPersonConfigFormData,
  ReportTemplateSentilinkApplicationRiskConfigFormData,
  ReportTemplateSentilinkScoresConfigFormData,
  ReportTemplateTrmWalletScreeningConfigFormData,
  ReportTemplateWatchlistConfigFormData,
} from 'dashboard/forms/FormReportTemplate';
import { Backdrop, Container } from 'dashboard/layouts/ShowViewLayoutV2';
import { ReportTemplate, ReportTemplateWatchlistMeta } from 'dashboard/models/report-template';
import {
  ReportTemplateVersion,
  ReportTemplateVersionCustomList,
} from 'dashboard/models/report-template-version';
import { ReportTemplateCustomListOverviewPage } from 'dashboard/views/DashboardReportTemplatesShowView/ReportTemplateCustomListOverviewPage';
import { ApiModel } from 'lib/interfaces/api';

const DISABLED_REPORT_TEMPLATES = [
  ApiModel.ReportTemplateAdverseMedia,
  ApiModel.ReportTemplateBusinessAdverseMedia,
  ApiModel.ReportTemplateBusinessAssociatedPersons,
  ApiModel.ReportTemplateBusinessWatchlist,
  ApiModel.ReportTemplatePoliticallyExposedPerson,
];

const GENERIC_HELP_CENTER_URL =
  'https://support.withpersona.com/hc/en-us/articles/8299941650707-Reports-Template-Editor-Overview';

interface DashboardReportTemplatesShowViewProps {
  reportTemplate: ReportTemplate;
  latestPublishedVersion: ReportTemplateVersion;
  reportTemplateVersion: ReportTemplateVersion;
  reportTemplateMeta: ReportTemplateWatchlistMeta;
  isLoading: boolean;
  canEdit: boolean;
  handleSaveReportTemplate: (formValues: ReportTemplateConfigFormData) => Promise<void>;
  refreshReportTemplate: () => Promise<void>;
}

const DashboardReportTemplatesShowView = ({
  reportTemplate,
  latestPublishedVersion,
  reportTemplateVersion,
  reportTemplateMeta,
  isLoading,
  canEdit,
  handleSaveReportTemplate,
}: DashboardReportTemplatesShowViewProps) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const defaultValues = useMemo<ReportTemplateConfigFormData>(() => {
    if (reportTemplate == null) {
      return undefined;
    }

    return {
      name: reportTemplate.attributes.name,
      ...latestPublishedVersion?.attributes,
    } as ReportTemplateConfigFormData;
  }, [reportTemplate, latestPublishedVersion]);

  if (reportTemplate == null) {
    return isLoading ? (
      <Backdrop>
        <LoadingIndicator fullscreen />
      </Backdrop>
    ) : (
      <Backdrop>
        <NullState
          message='We couldn’t find that Report Template.'
          backRoute='/dashboard/report-templates'
        />
      </Backdrop>
    );
  }

  return (
    <>
      <Helmet title={`${reportTemplate.attributes.name} - Templates - Reports`} />
      {reportTemplate.type === ApiModel.ReportTemplateCustomList ? (
        // Show special overview page only for Custom List report templates
        <ReportTemplateCustomListOverviewPage
          reportTemplate={reportTemplate}
          latestPublishedVersion={latestPublishedVersion as ReportTemplateVersionCustomList}
          reportTemplateVersion={reportTemplateVersion as ReportTemplateVersionCustomList}
        />
      ) : (
        <Backdrop>
          <Container>
            {renderReportTemplateForm(
              reportTemplate.type,
              {
                canEdit: canEdit,
                canEditConfig: !DISABLED_REPORT_TEMPLATES.includes(reportTemplate.type),
                helpCenterUrl: HELP_CENTER_URLS[reportTemplate.type] ?? GENERIC_HELP_CENTER_URL,
                isLoadingPage: isLoading,
                latestPublishedVersionId: latestPublishedVersion.id,
                submit: handleSaveReportTemplate,
              },
              defaultValues,
              reportTemplateMeta,
            )}
          </Container>
        </Backdrop>
      )}
    </>
  );
};

const renderReportTemplateForm = (
  templateType: ApiModel,
  props: {
    canEdit: boolean;
    canEditConfig: boolean;
    submit: (formValues: ReportTemplateConfigFormData) => Promise<void>;
    isLoadingPage: boolean;
    latestPublishedVersionId: string;
    helpCenterUrl?: string;
  },
  defaultValues: ReportTemplateConfigFormData,
  reportTemplateMeta?: ReportTemplateWatchlistMeta,
) => {
  switch (templateType) {
    case ApiModel.ReportTemplateAdverseMedia:
      return (
        <FormReportTemplateAdverseMedia
          {...props}
          defaultValues={defaultValues as ReportTemplateAdverseMediaConfigFormData}
        />
      );
    case ApiModel.ReportTemplateBusinessAdverseMedia:
      return (
        <FormReportTemplateBusinessAdverseMedia
          {...props}
          defaultValues={defaultValues as ReportTemplateBusinessAdverseMediaConfigFormData}
        />
      );
    case ApiModel.ReportTemplateBusinessClassification:
      return (
        <FormReportTemplateBusinessClassification
          {...props}
          defaultValues={defaultValues as ReportTemplateBusinessClassificationConfigFormData}
        />
      );
    case ApiModel.ReportTemplateBusinessWatchlist:
      return (
        <FormReportTemplateBusinessWatchlist
          {...props}
          defaultValues={defaultValues as ReportTemplateBusinessWatchlistConfigFormData}
        />
      );
    case ApiModel.ReportTemplateChainalysisAddressScreening:
      return (
        <FormReportTemplateChainalysisAddressScreening
          {...props}
          defaultValues={defaultValues as ReportTemplateChainalysisAddressScreeningConfigFormData}
        />
      );
    case ApiModel.ReportTemplateCryptoAddressWatchlist:
      return (
        <FormReportTemplateCryptoAddressWatchlist
          {...props}
          defaultValues={defaultValues as ReportTemplateCryptoAddressWatchlistConfigFormData}
        />
      );
    case ApiModel.ReportTemplateEquifaxOneview:
      return (
        <FormReportTemplateEquifaxOneview
          {...props}
          defaultValues={defaultValues as ReportTemplateEquifaxOneviewConfigFormData}
        />
      );
    case ApiModel.ReportTemplateKyckrBusinessLookup:
      return (
        <FormReportTemplateKyckrBusinessLookup
          {...props}
          defaultValues={defaultValues as ReportTemplateKyckrConfigFormData}
        />
      );
    case ApiModel.ReportTemplateMiddeskBusinessLookup:
      return (
        <FormReportTemplateMiddeskBusinessLookup
          {...props}
          defaultValues={defaultValues as ReportTemplateMiddeskConfigFormData}
        />
      );
    case ApiModel.ReportTemplateMxAccount:
      return (
        <FormReportTemplateMxAccount
          {...props}
          defaultValues={defaultValues as ReportTemplateMxAccountConfigFormData}
        />
      );
    case ApiModel.ReportTemplatePoliticallyExposedPerson:
      return (
        <FormReportTemplatePoliticallyExposedPerson
          {...props}
          defaultValues={defaultValues as ReportTemplatePoliticallyExposedPersonConfigFormData}
        />
      );
    case ApiModel.ReportTemplateSentilinkApplicationRisk:
      return (
        <FormReportTemplateSentilinkApplicationRisk
          {...props}
          defaultValues={defaultValues as ReportTemplateSentilinkApplicationRiskConfigFormData}
        />
      );
    case ApiModel.ReportTemplateSentilinkScores:
      return (
        <FormReportTemplateSentilinkScores
          {...props}
          defaultValues={defaultValues as ReportTemplateSentilinkScoresConfigFormData}
        />
      );
    case ApiModel.ReportTemplateTrmWalletScreening:
      return (
        <FormReportTemplateTrmWalletScreening
          {...props}
          defaultValues={defaultValues as ReportTemplateTrmWalletScreeningConfigFormData}
        />
      );
    case ApiModel.ReportTemplateWatchlist:
      return (
        <FormReportTemplateWatchlist
          {...props}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          meta={reportTemplateMeta}
          defaultValues={defaultValues as ReportTemplateWatchlistConfigFormData}
        />
      );
    default:
      return (
        <FormReportTemplateGeneric
          {...props}
          defaultValues={defaultValues as ReportTemplateGenericConfigFormData}
        />
      );
  }
};

const HELP_CENTER_URLS: { [key in ReportTemplate['type']]?: string } = {
  [ApiModel.ReportTemplateAdverseMedia]:
    'https://support.withpersona.com/hc/en-us/articles/11487699261843-How-to-Use-Adverse-Media-Report-Template-Editor',
  [ApiModel.ReportTemplateBusinessAdverseMedia]:
    'https://support.withpersona.com/hc/en-us/articles/11681199248531',
  [ApiModel.ReportTemplateBusinessWatchlist]:
    'https://support.withpersona.com/hc/en-us/articles/11497413242643-How-to-Use-Business-Watchlist-RTE',
  [ApiModel.ReportTemplatePoliticallyExposedPerson]:
    'https://support.withpersona.com/hc/en-us/articles/11496817500307-How-to-Use-Politically-Exposed-Person-RTE',
  [ApiModel.ReportTemplateWatchlist]:
    'https://support.withpersona.com/hc/en-us/articles/8300337537811-How-to-Use-Watchlist-Report-Template-Editor',
};

export default DashboardReportTemplatesShowView;
